import {
  type AssignableProjectRole,
  assignableProjectRoleSchema,
  type ProjectRole,
} from "@esi/shared";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

/** The one width of a role column, picker or static label, so rows line up. */
const ROLE_COLUMN = "w-52";

/**
 * Picks a project role: one option per role the caller may assign, each with its name and a
 * one-line description. `owner` is never offered (only a transfer of ownership makes an owner);
 * when the current value is `owner` it shows as the selected value but cannot be picked.
 */
export function RolePicker(props: {
  value: ProjectRole;
  onChange: (role: AssignableProjectRole) => void;
  id?: string;
  "aria-label"?: string;
  disabled?: boolean;
  size?: "sm" | "default";
  className?: string;
}) {
  const { t } = useTranslation();
  const current = props.value === "owner" ? (["owner"] as const) : [];
  return (
    <Select
      value={props.value}
      disabled={props.disabled}
      onValueChange={(next) => {
        const role = assignableProjectRoleSchema.safeParse(next);
        if (role.success && role.data !== props.value) {
          props.onChange(role.data);
        }
      }}
    >
      <SelectTrigger
        id={props.id}
        aria-label={props["aria-label"]}
        size={props.size}
        className={cn(ROLE_COLUMN, props.className)}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" align="end" className="w-72 max-w-[calc(100vw-2rem)]">
        {current.map((role) => (
          <SelectItem key={role} value={role} disabled>
            {t(`projectRoles.${role}.name`)}
          </SelectItem>
        ))}
        {assignableProjectRoleSchema.options.map((role) => (
          <SelectItem key={role} value={role} description={t(`projectRoles.${role}.description`)}>
            {t(`projectRoles.${role}.name`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * A role shown, not offered: the owner's row, emergency access, a read-only viewer. Same width
 * and text inset as a small picker, so a column of mixed rows stays aligned.
 */
export function RoleLabel(props: { role: ProjectRole; icon?: ReactNode; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "inline-flex h-8 shrink-0 items-center gap-1.5 border border-transparent px-3 text-sm text-fg-secondary",
        ROLE_COLUMN,
        props.className,
      )}
    >
      {props.icon}
      <span className="truncate">{t(`projectRoles.${props.role}.name`)}</span>
    </span>
  );
}
